import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Target, TrendingUp, Apple, Dumbbell, Calendar, MessageSquare } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';

const PersonalTraining = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    privacyConsent: false
  });

  const benefits = [
    {
      icon: Target,
      title: 'Individuelle Ziele',
      description: 'Maßgeschneiderter Trainingsplan für Ihre persönlichen Ziele'
    },
    {
      icon: TrendingUp,
      title: 'Leistungssteigerung',
      description: 'Optimierung von Kraft, Ausdauer und Beweglichkeit'
    },
    {
      icon: Apple,
      title: 'Ernährungsberatung',
      description: 'Alltagsnahe Tipps für gesunde Ernährung'
    },
    {
      icon: Dumbbell,
      title: 'Flexibles Training',
      description: 'Training in der Praxis oder extern im Gym'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.privacyConsent) {
      toast({
        title: 'Datenschutz-Einwilligung erforderlich',
        description: 'Bitte bestätigen Sie, dass Sie die Datenschutzerklärung gelesen haben.',
        variant: 'destructive'
      });
      return;
    }

    // Mock form submission - in production this would send to physio.grau@gmail.com
    toast({
      title: 'Anfrage gesendet',
      description: 'Wir melden uns schnellstmöglich bei Ihnen.',
    });
    setFormData({ name: '', email: '', phone: '', message: '', privacyConsent: false });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (checked) => {
    setFormData({ ...formData, privacyConsent: checked });
  };

  return (
    <div className="page-container pt-page">
      <section className="page-hero pt-hero">
        <div className="section-container">
          <div className="pt-hero-content">
            <div className="pt-hero-text">
              <h1 className="page-title">Personal Training</h1>
              <p className="page-subtitle">
                Individuelles Training für Ihre persönlichen Ziele
              </p>
              <div className="pt-hero-notice">
                <p className="notice-badge">Selbstzahlerleistung</p>
              </div>
            </div>
            <div className="pt-hero-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_physiograu/artifacts/sc0xj02g_IMG_7220.JPG" 
                alt="Personal Training Bereich"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="page-content">
        <div className="section-container">
          <div className="pt-intro">
            <h2 className="subsection-title">Ihr persönlicher Trainingsplan</h2>
            <p className="text-content">
              Personal Training ist keine Therapie, sondern richtet sich an gesunde Menschen, 
              die ihre Fitness verbessern, ihre Leistung steigern oder präventiv etwas für 
              ihre Gesundheit tun möchten.
            </p>
            <p className="text-content">
              Als Selbstzahlerleistung bietet Personal Training maximale Flexibilität und 
              individuelle Betreuung für Ihre persönlichen Trainingsziele.
            </p>
          </div>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className="pt-benefit-card">
                  <CardContent className="benefit-card-content">
                    <IconComponent className="benefit-icon" />
                    <h3 className="benefit-title">{benefit.title}</h3>
                    <p className="benefit-description">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="pt-details">
            <div className="pt-detail-section">
              <h3 className="subsection-subtitle">Leistungen im Personal Training</h3>
              <ul className="content-list">
                <li>1:1 Personal Training mit individueller Betreuung</li>
                <li>Individuelle Trainingsplanung und -steuerung</li>
                <li>Erstellung von Heimtrainingsprogrammen</li>
                <li>Training in unserer Praxis oder extern im Fitnessstudio</li>
                <li>Unterstützung bei Gewichtsregulation</li>
                <li>Vermittlung von Grundlagen zu Training und Regeneration</li>
                <li>Alltagsnahe Ernährungsberatung</li>
              </ul>
            </div>

            <div className="pt-images-grid">
              <img 
                src="https://customer-assets.emergentagent.com/job_physiograu/artifacts/3taktmyj_IMG_7181.JPG" 
                alt="Trainingsbereich"
                className="pt-detail-image"
              />
            </div>
          </div>

          <div className="pt-process">
            <h3 className="subsection-subtitle">Ihr Weg zum Personal Training</h3>
            <div className="process-grid">
              <Card className="process-card">
                <CardContent className="process-card-content">
                  <div className="process-number">1</div>
                  <h4 className="process-title">Erstgespräch</h4>
                  <p className="process-description">
                    Kennenlernen und Besprechung Ihrer Ziele
                  </p>
                </CardContent>
              </Card>
              <Card className="process-card">
                <CardContent className="process-card-content">
                  <div className="process-number">2</div>
                  <h4 className="process-title">Analyse</h4>
                  <p className="process-description">
                    Erfassung Ihres aktuellen Leistungsstands
                  </p>
                </CardContent>
              </Card>
              <Card className="process-card">
                <CardContent className="process-card-content">
                  <div className="process-number">3</div>
                  <h4 className="process-title">Planung</h4>
                  <p className="process-description">
                    Erstellung Ihres individuellen Trainingsplans
                  </p>
                </CardContent>
              </Card>
              <Card className="process-card">
                <CardContent className="process-card-content">
                  <div className="process-number">4</div>
                  <h4 className="process-title">Training</h4>
                  <p className="process-description">
                    Umsetzung mit persönlicher Begleitung
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="pt-important">
            <Card className="important-card">
              <CardContent className="important-content">
                <h3 className="subsection-subtitle">Wichtige Hinweise</h3>
                <p className="text-content">
                  Personal Training ist eine Selbstzahlerleistung und wird nicht von den 
                  Krankenkassen übernommen. Es richtet sich an gesunde Menschen zur 
                  Prävention und Leistungssteigerung.
                </p>
                <p className="text-content">
                  Die Ernährungsberatung erfolgt alltagsnah und beratend. Es handelt sich 
                  nicht um eine medizinische Ernährungstherapie.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="pt-contact-form">
            <h3 className="subsection-subtitle">Anfrage für Personal Training</h3>
            <Card className="form-card">
              <CardContent className="form-card-content">
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Name *</label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Ihr Name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">E-Mail *</label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="ihre.email@beispiel.de"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Telefon</label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Ihre Telefonnummer"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Ihre Nachricht *</label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Erzählen Sie uns von Ihren Trainingszielen..."
                      rows={5}
                    />
                  </div>
                  <div className="form-group">
                    <div className="privacy-consent">
                      <Checkbox
                        id="privacyConsent"
                        checked={formData.privacyConsent}
                        onCheckedChange={handleCheckboxChange}
                        required
                      />
                      <label 
                        htmlFor="privacyConsent" 
                        className="privacy-label"
                      >
                        Ich habe die{' '}
                        <Link to="/datenschutz" className="privacy-link" target="_blank">
                          Datenschutzerklärung
                        </Link>
                        {' '}gelesen und stimme der Verarbeitung meiner Angaben zur Bearbeitung 
                        meiner Anfrage zu. *
                      </label>
                    </div>
                  </div>
                  <Button type="submit" size="lg" className="submit-button">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Anfrage senden
                  </Button>
                  <p className="form-note">
                    Ihre Anfrage wird ausschließlich an physio.grau@gmail.com gesendet und 
                    dort verarbeitet. Es erfolgt keine Speicherung in externen Systemen.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PersonalTraining;